import { useState } from 'react';
import { ChevronLeft, ChevronRight, Home, Book, Maximize2 } from 'lucide-react';
import { Link } from 'wouter';
import { SignedIn, SignedOut, RedirectToSignIn } from '@clerk/clerk-react';

interface SlideData {
  id: number;
  title: string;
  content: JSX.Element;
}

const KeyTakeaway = ({ children }: { children: React.ReactNode }) => (
  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-4 my-4 rounded-r-lg">
    <div className="flex">
      <div className="flex-shrink-0">
        <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      </div>
      <div className="ml-3">
        <p className="text-sm text-blue-700 font-medium">
          <strong>Key Takeaway:</strong> {children}
        </p>
      </div>
    </div>
  </div>
);

export default function PeptidesShiftApproach() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const slides: SlideData[] = [
    {
      id: 1,
      title: "Therapeutic Peptides in Longevity Medicine: A SHIFT Framework Review",
      content: (
        <div className="text-center space-y-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">
            Therapeutic Peptides in Longevity Medicine: A SHIFT Framework Review
          </h1>
          
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-8 rounded-lg border border-blue-200">
            <p className="text-xl text-gray-700 mb-4">Targeting Fundamental Aging Processes with Precision Medicine</p>
            <p className="text-lg text-gray-600">A comprehensive approach to peptide therapeutics</p>
          </div>
          
          <div className="flex justify-center">
            <svg width="300" height="200" viewBox="0 0 300 200" className="border border-gray-200 rounded-lg">
              {/* SHIFT Framework Visual */}
              <circle cx="150" cy="100" r="80" fill="none" stroke="#3b82f6" strokeWidth="2" strokeDasharray="5,5" />
              
              <circle cx="150" cy="50" r="20" fill="#ef4444" opacity="0.7" />
              <text x="150" y="55" textAnchor="middle" className="fill-white font-semibold text-xs">S</text>
              
              <circle cx="200" cy="80" r="20" fill="#10b981" opacity="0.7" />
              <text x="200" y="85" textAnchor="middle" className="fill-white font-semibold text-xs">H</text>
              
              <circle cx="200" cy="120" r="20" fill="#f59e0b" opacity="0.7" />
              <text x="200" y="125" textAnchor="middle" className="fill-white font-semibold text-xs">I</text>
              
              <circle cx="150" cy="150" r="20" fill="#8b5cf6" opacity="0.7" />
              <text x="150" y="155" textAnchor="middle" className="fill-white font-semibold text-xs">F</text>
              
              <circle cx="100" cy="120" r="20" fill="#06b6d4" opacity="0.7" />
              <text x="100" y="125" textAnchor="middle" className="fill-white font-semibold text-xs">T</text>
              
              <text x="150" y="180" textAnchor="middle" className="fill-gray-600 font-semibold text-sm">SHIFT Framework</text>
            </svg>
          </div>
        </div>
      )
    },
    {
      id: 2,
      title: "Introduction: Peptides as Precision Longevity Tools",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Introduction: Peptides as Precision Longevity Tools</h2>
          
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-2">What are Therapeutic Peptides?</h3>
              <p className="text-blue-700">Short chains of amino acids that target fundamental aging processes with high specificity and minimal toxicity</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">Why Use the SHIFT Framework?</h3>
              <p className="text-green-700">Organizes peptide therapies by their effects on aging hallmarks: Sensing, Homeostatic Defense, Inflammation, Fibrosis, Timekeeping</p>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h3 className="font-semibold text-purple-800 mb-2">Clinical Approach</h3>
              <p className="text-purple-700">Evidence-based protocols for targeting age-related dysfunctions through peptide interventions</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Therapeutic peptides offer a multi-pronged strategy to extend healthspan by restoring youthful signaling networks with high specificity and low toxicity.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 3,
      title: "SHIFT Framework Overview",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">SHIFT Framework Overview</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h3 className="font-semibold text-red-800 mb-2 flex items-center">
                  <span className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-sm mr-2">S</span>
                  Sensing
                </h3>
                <p className="text-red-700 text-sm">Nutrient & growth sensing dysregulation</p>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2 flex items-center">
                  <span className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-sm mr-2">H</span>
                  Homeostatic Defense
                </h3>
                <p className="text-green-700 text-sm">Repair, immunity & proteostasis</p>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h3 className="font-semibold text-yellow-800 mb-2 flex items-center">
                  <span className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center text-white text-sm mr-2">I</span>
                  Inflammation
                </h3>
                <p className="text-yellow-700 text-sm">Mitigating "inflammaging"</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2 flex items-center">
                  <span className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm mr-2">F</span>
                  Fibrosis
                </h3>
                <p className="text-purple-700 text-sm">Anti-fibrotic & tissue remodeling</p>
              </div>
              
              <div className="bg-cyan-50 p-4 rounded-lg border border-cyan-200">
                <h3 className="font-semibold text-cyan-800 mb-2 flex items-center">
                  <span className="w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center text-white text-sm mr-2">T</span>
                  Timekeeping
                </h3>
                <p className="text-cyan-700 text-sm">Telomeres & circadian regulation</p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h3 className="font-semibold text-gray-800 mb-2">Integrated Approach</h3>
                <p className="text-gray-700 text-sm">Target multiple aging mechanisms simultaneously</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 4,
      title: "Peptides at a Glance: Summary Table",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Peptides at a Glance: Summary Table</h2>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Peptide</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">SHIFT Domain</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Key Mechanisms</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Typical Dosing</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-2 py-2 font-medium text-gray-900">GLP-1 RAs</td>
                  <td className="px-2 py-2 text-gray-700">Sensing, Inflammation</td>
                  <td className="px-2 py-2 text-gray-700">GLP-1 receptor activation</td>
                  <td className="px-2 py-2 text-gray-700">0.25-2.4mg weekly (SC)</td>
                  <td className="px-2 py-2 text-gray-700">FDA-approved</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-2 py-2 font-medium text-gray-900">Tesamorelin</td>
                  <td className="px-2 py-2 text-gray-700">Sensing, Homeostatic</td>
                  <td className="px-2 py-2 text-gray-700">GHRH analog, ↑IGF-1</td>
                  <td className="px-2 py-2 text-gray-700">1mg SC nightly</td>
                  <td className="px-2 py-2 text-gray-700">FDA-approved (HIV)</td>
                </tr>
                <tr>
                  <td className="px-2 py-2 font-medium text-gray-900">Ipamorelin</td>
                  <td className="px-2 py-2 text-gray-700">Sensing</td>
                  <td className="px-2 py-2 text-gray-700">Ghrelin receptor agonist</td>
                  <td className="px-2 py-2 text-gray-700">100-300µg 1-3x daily</td>
                  <td className="px-2 py-2 text-gray-700">Research use</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-2 py-2 font-medium text-gray-900">Thymosin α1</td>
                  <td className="px-2 py-2 text-gray-700">Homeostatic, Inflammation</td>
                  <td className="px-2 py-2 text-gray-700">T-cell enhancement</td>
                  <td className="px-2 py-2 text-gray-700">1.6mg 2x/week</td>
                  <td className="px-2 py-2 text-gray-700">Approved overseas</td>
                </tr>
                <tr>
                  <td className="px-2 py-2 font-medium text-gray-900">BPC-157</td>
                  <td className="px-2 py-2 text-gray-700">Homeostatic, Fibrosis</td>
                  <td className="px-2 py-2 text-gray-700">Tissue repair, angiogenesis</td>
                  <td className="px-2 py-2 text-gray-700">200-500µg daily</td>
                  <td className="px-2 py-2 text-gray-700">Research use</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-2 py-2 font-medium text-gray-900">TB-500</td>
                  <td className="px-2 py-2 text-gray-700">Fibrosis, Homeostatic</td>
                  <td className="px-2 py-2 text-gray-700">Actin dynamics, anti-fibrotic</td>
                  <td className="px-2 py-2 text-gray-700">5-10mg weekly</td>
                  <td className="px-2 py-2 text-gray-700">Research use</td>
                </tr>
                <tr>
                  <td className="px-2 py-2 font-medium text-gray-900">Epitalon</td>
                  <td className="px-2 py-2 text-gray-700">Timekeeping</td>
                  <td className="px-2 py-2 text-gray-700">Telomerase activation</td>
                  <td className="px-2 py-2 text-gray-700">5-10mg daily x10 days</td>
                  <td className="px-2 py-2 text-gray-700">Research use</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    },
    {
      id: 5,
      title: "Sensing Domain: GLP-1 Receptor Agonists",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Sensing Domain: GLP-1 Receptor Agonists</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">Primary Mechanisms:</h3>
                <ul className="text-blue-700 space-y-1 text-sm">
                  <li>• Activate GLP-1 receptors</li>
                  <li>• Enhance glucose-dependent insulin secretion</li>
                  <li>• Suppress glucagon release</li>
                  <li>• Reduce metabolic stress and inflammation</li>
                </ul>
              </div>
              
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h3 className="font-semibold text-indigo-800 mb-2">Key Signaling Pathways:</h3>
                <ul className="text-indigo-700 space-y-1 text-sm">
                  <li>• <strong>cAMP-PKA-CREB pathway</strong> → insulin secretion, neuronal survival</li>
                  <li>• <strong>NF-κB inhibition</strong> → reduced inflammatory cytokines</li>
                  <li>• <strong>PI3K-Akt</strong> → cellular survival, glucose uptake</li>
                  <li>• <strong>AMPK activation (indirect)</strong> → mitochondrial biogenesis</li>
                </ul>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2">Clinical Evidence:</h3>
                <ul className="text-green-700 space-y-1 text-sm">
                  <li>• 10-15% weight loss in obesity trials</li>
                  <li>• Improved glycemic control</li>
                  <li>• 50% lower dementia risk in diabetics</li>
                  <li>• Reduced CV events</li>
                </ul>
              </div>
            </div>
            
            <div className="flex items-center justify-center">
              <svg width="280" height="300" viewBox="0 0 280 300" className="border border-gray-200 rounded-lg">
                <rect x="90" y="20" width="100" height="40" fill="#3b82f6" opacity="0.7" rx="8" />
                <text x="140" y="45" textAnchor="middle" className="fill-white font-semibold text-sm">GLP-1 RA</text>
                
                <rect x="20" y="90" width="80" height="35" fill="#10b981" opacity="0.7" rx="6" />
                <text x="60" y="110" textAnchor="middle" className="fill-white font-semibold text-xs">Insulin↑</text>
                
                <rect x="180" y="90" width="80" height="35" fill="#ef4444" opacity="0.7" rx="6" />
                <text x="220" y="110" textAnchor="middle" className="fill-white font-semibold text-xs">Glucagon↓</text>
                
                <rect x="20" y="150" width="80" height="35" fill="#f59e0b" opacity="0.7" rx="6" />
                <text x="60" y="170" textAnchor="middle" className="fill-white font-semibold text-xs">Weight↓</text>
                
                <rect x="180" y="150" width="80" height="35" fill="#8b5cf6" opacity="0.7" rx="6" />
                <text x="220" y="170" textAnchor="middle" className="fill-white font-semibold text-xs">CRP↓</text>
                
                <rect x="90" y="220" width="100" height="50" fill="#22c55e" opacity="0.7" rx="8" />
                <text x="140" y="240" textAnchor="middle" className="fill-white font-semibold text-xs">Metabolic</text>
                <text x="140" y="255" textAnchor="middle" className="fill-white font-semibold text-xs">Optimization</text>
              </svg>
            </div>
          </div>
          
          <KeyTakeaway>
            GLP-1 RAs re-sensitize nutrient signaling pathways and attack the root of insulin resistance - a major age-related dysfunction with FDA approval for diabetes and obesity.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 6,
      title: "GLP-1 RAs: Clinical Protocol",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">GLP-1 RAs: Clinical Protocol</h2>
          
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parameter</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Semaglutide</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Liraglutide</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Starting Dose</td>
                  <td className="px-6 py-4 text-sm text-gray-700">0.25mg weekly</td>
                  <td className="px-6 py-4 text-sm text-gray-700">0.6mg daily</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Target Dose</td>
                  <td className="px-6 py-4 text-sm text-gray-700">2.4mg weekly</td>
                  <td className="px-6 py-4 text-sm text-gray-700">3.0mg daily</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Titration</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Increase monthly</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Increase weekly</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Common Side Effects</td>
                  <td className="px-6 py-4 text-sm text-gray-700" colSpan={2}>Nausea, GI upset, appetite loss (usually transient)</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Monitoring</td>
                  <td className="px-6 py-4 text-sm text-gray-700" colSpan={2}>HbA1c, weight, lipids, amylase/lipase</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Longevity Use</td>
                  <td className="px-6 py-4 text-sm text-gray-700" colSpan={2}>Off-label for optimal metabolic biomarkers (HbA1c {'<'}5.5%)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    },
    {
      id: 7,
      title: "Growth Hormone Secretagogues",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Growth Hormone Secretagogues</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2">GHRH Analogs (Tesamorelin):</h3>
                <ul className="text-purple-700 space-y-1 text-sm">
                  <li>• <strong>GHRH–cAMP–PKA–CREB</strong> → stimulates GH production</li>
                  <li>• <strong>GH–JAK2–STAT5</strong> → activates hepatic IGF-1 synthesis</li>
                  <li>• <strong>IGF-1–PI3K–Akt–mTOR</strong> → anabolic processes, cell survival</li>
                  <li>• Preserve normal feedback control, reduce visceral fat</li>
                </ul>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2">GHRPs (Ipamorelin):</h3>
                <ul className="text-green-700 space-y-1 text-sm">
                  <li>• <strong>GHS-R1a–PLC–IP3/Ca²⁺</strong> → GH secretion without cortisol elevation</li>
                  <li>• <strong>GH–JAK2–STAT5 and PI3K–Akt</strong> → downstream anabolic effects</li>
                  <li>• <strong>Indirect AMPK modulation</strong> → mitochondrial efficiency enhancement</li>
                  <li>• Selective ghrelin receptor agonist, minimal appetite effect</li>
                </ul>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">Clinical Evidence:</h3>
                <ul className="text-blue-700 space-y-1 text-sm">
                  <li>• Increased lean mass, reduced fat mass</li>
                  <li>• Cognitive improvements (memory, executive function)</li>
                  <li>• NAFLD prevention in HIV patients</li>
                  <li>• Improved liver biology and inflammation</li>
                </ul>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h3 className="font-semibold text-yellow-800 mb-2">Safety Considerations:</h3>
                <ul className="text-yellow-700 space-y-1 text-sm">
                  <li>• Monitor IGF-1 levels (mid-high normal)</li>
                  <li>• Glucose monitoring (GH is counter-insulin)</li>
                  <li>• Cancer history considerations</li>
                  <li>• Cycling protocols recommended</li>
                </ul>
              </div>
            </div>
          </div>
          
          <KeyTakeaway>
            GH secretagogues restore youthful anabolism and repair capacity while preserving natural feedback control, making them safer than direct GH administration.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 8,
      title: "MOTS-c: Mitochondrial Exercise Mimetic",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">MOTS-c: Mitochondrial Exercise Mimetic</h2>
          
          <div className="space-y-4">
            <div className="bg-cyan-50 p-4 rounded-lg border border-cyan-200">
              <h3 className="font-semibold text-cyan-800 mb-2">Mechanisms:</h3>
              <p className="text-cyan-700">16-aa peptide encoded in mtDNA that activates AMPK and metabolic stress response genes, improving insulin sensitivity and muscle adaptation</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">Preclinical Evidence:</h3>
              <ul className="text-green-700 space-y-1">
                <li>• Improved insulin sensitivity in obese/aged mice</li>
                <li>• Enhanced running capacity across lifespan</li>
                <li>• Extended healthspan when started late in life</li>
                <li>• Promotes fat utilization, mitigates insulin resistance</li>
              </ul>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-2">Human Data & Development:</h3>
              <ul className="text-blue-700 space-y-1">
                <li>• Exercise raises MOTS-c levels naturally</li>
                <li>• Levels decline with age and metabolic disease</li>
                <li>• CB4211 analog in Phase 1b for NAFLD/obesity</li>
                <li>• Banned as performance enhancer in athletics</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <h3 className="font-semibold text-orange-800 mb-2">Experimental Use:</h3>
            <p className="text-orange-700">~5-10mg SC 1-2x weekly (extrapolated from mouse studies). Not commercially available or FDA-approved - research use only with unknown safety profile.</p>
          </div>
          
          <KeyTakeaway>
            MOTS-c represents a potential "exercise mimetic" that could treat diabetes, obesity, and immunosenescence by reducing inflammatory cytokines and restoring youthful energy utilization.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 9,
      title: "Homeostatic Defense: Thymosin Alpha-1",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Homeostatic Defense: Thymosin Alpha-1</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">Primary Mechanisms:</h3>
                <ul className="text-blue-700 space-y-1 text-sm">
                  <li>• Enhances T lymphocyte maturation</li>
                  <li>• Promotes robust Th1 response</li>
                  <li>• Increases NK cell activity</li>
                  <li>• Modulates TLR pathways</li>
                </ul>
              </div>
              
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h3 className="font-semibold text-indigo-800 mb-2">Key Signaling Pathways:</h3>
                <ul className="text-indigo-700 space-y-1 text-sm">
                  <li>• <strong>TLR–MyD88–IRAK–NF-κB</strong> → boosts innate immune signaling</li>
                  <li>• <strong>STAT1/STAT3 signaling</strong> → enhances IL-2, IFN-γ production</li>
                  <li>• <strong>Restores MHC class I expression</strong> → improves antigen presentation</li>
                  <li>• Enhanced T cell proliferation and antiviral response</li>
                </ul>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2">Clinical Applications:</h3>
                <ul className="text-green-700 space-y-1 text-sm">
                  <li>• Hepatitis B/C (approved overseas)</li>
                  <li>• Cancer immunotherapy adjuvant</li>
                  <li>• COVID-19 (reduced mortality)</li>
                  <li>• Sepsis and infections</li>
                </ul>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2">Aging Applications:</h3>
                <ul className="text-purple-700 space-y-1 text-sm">
                  <li>• Combats immunosenescence</li>
                  <li>• Improves vaccine responsiveness</li>
                  <li>• Reduces chronic viral burden</li>
                  <li>• Decreases auto-inflammatory markers</li>
                </ul>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h3 className="font-semibold text-yellow-800 mb-2">Protocol:</h3>
                <ul className="text-yellow-700 space-y-1 text-sm">
                  <li>• 1.6mg SC twice weekly</li>
                  <li>• 1-3 month cycles</li>
                  <li>• Very well tolerated</li>
                  <li>• Mild injection site reactions only</li>
                </ul>
              </div>
            </div>
          </div>
          
          <KeyTakeaway>
            TA1 "reboots" a flagging immune system and helps restore a more youthful immune profile that can defend against infections and neoplastic cells while reducing chronic inflammation.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 10,
      title: "BPC-157: Body Protection Compound",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">BPC-157: Body Protection Compound</h2>
          
          <div className="space-y-4">
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <h3 className="font-semibold text-red-800 mb-2">Primary Mechanisms:</h3>
              <p className="text-red-700">15-aa peptide that promotes angiogenesis, regulates growth factors (VEGF, FGF), modulates nitric oxide, and has anti-inflammatory activity</p>
            </div>
            
            <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
              <h3 className="font-semibold text-pink-800 mb-2">Key Signaling Pathways:</h3>
              <ul className="text-pink-700 space-y-1 text-sm">
                <li>• <strong>VEGF–VEGFR2–MAPK/ERK</strong> → promotes angiogenesis</li>
                <li>• <strong>NO synthase pathways (eNOS, iNOS)</strong> → enhances vascular tone</li>
                <li>• <strong>Focal adhesion kinase (FAK)</strong> → fibroblast migration, ECM remodeling</li>
                <li>• <strong>Akt–mTOR and Sirt1 modulation</strong> → healing and metabolic regulation</li>
              </ul>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">Broad-Spectrum Healing:</h3>
              <ul className="text-green-700 space-y-1">
                <li>• Musculoskeletal injuries (faster, stronger healing)</li>
                <li>• Gastric ulcers and IBD protection</li>
                <li>• Liver protection from toxic injury</li>
                <li>• Prevents excessive scar formation</li>
              </ul>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-2">Clinical Use:</h3>
              <ul className="text-blue-700 space-y-1">
                <li>• SC near injury: 250µg daily x 4-6 weeks</li>
                <li>• Systemic/oral: 500-1000µg daily</li>
                <li>• Stable in gastric acid</li>
                <li>• Improvement typically within 1-2 weeks</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <h3 className="font-semibold text-yellow-800 mb-2">Safety & Status:</h3>
            <p className="text-yellow-700">Extremely safe with no defined LD50. Phase II IBD trial positive. Not FDA-approved; banned from US compounding 2023. Prohibited in professional sports.</p>
          </div>
          
          <KeyTakeaway>
            BPC-157 exemplifies a potent homeostatic defense peptide that protects and repairs tissues, counteracting impaired healing and chronic micro-injuries that accumulate with age.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 11,
      title: "Inflammation Domain: Anti-Inflammaging Strategies",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Inflammation Domain: Anti-Inflammaging Strategies</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">Thymosin α1 - Immune Modulation:</h3>
                <ul className="text-blue-700 space-y-1 text-sm">
                  <li>• Normalizes inflammatory processes</li>
                  <li>• Shifts cytokine profiles (↑IL-2, IFN-γ)</li>
                  <li>• Promotes T_reg cells</li>
                  <li>• Reduces CRP, IL-6 in aging</li>
                </ul>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2">GLP-1 RAs - Metabolic Anti-Inflammation:</h3>
                <ul className="text-green-700 space-y-1 text-sm">
                  <li>• Reduces visceral fat inflammation</li>
                  <li>• Attenuates oxidative stress</li>
                  <li>• Lowers CRP and inflammatory interleukins</li>
                  <li>• Reduces neuroinflammation</li>
                </ul>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2">Other Anti-Inflammatory Peptides:</h3>
                <ul className="text-purple-700 space-y-1 text-sm">
                  <li>• KPV: NF-κB inhibition (IBD, skin)</li>
                  <li>• LL-37: Antimicrobial, immune-modulating</li>
                  <li>• Target specific inflammatory pathways</li>
                </ul>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h3 className="font-semibold text-yellow-800 mb-2">Clinical Outcomes:</h3>
                <ul className="text-yellow-700 space-y-1 text-sm">
                  <li>• Reduced atherosclerotic events</li>
                  <li>• Improved cognitive function</li>
                  <li>• Enhanced energy and well-being</li>
                  <li>• Lower chronic disease risk</li>
                </ul>
              </div>
            </div>
          </div>
          
          <KeyTakeaway>
            Controlling chronic inflammation is key to longevity. Peptides provide tools to address root causes (immune senescence, visceral adiposity) rather than just blocking inflammation symptomatically.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 12,
      title: "Fibrosis Domain: Thymosin Beta-4 and TB-500",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Fibrosis Domain: Thymosin Beta-4 and TB-500</h2>
          
          <div className="space-y-4">
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h3 className="font-semibold text-purple-800 mb-2">Primary Mechanisms:</h3>
              <p className="text-purple-700">43-aa peptide that regulates actin cytoskeleton and contains Ac-SDKP fragment that inhibits TGF-β-driven fibroblast activation and collagen deposition</p>
            </div>
            
            <div className="bg-cyan-50 p-4 rounded-lg border border-cyan-200">
              <h3 className="font-semibold text-cyan-800 mb-2">Key Signaling Pathways:</h3>
              <ul className="text-cyan-700 space-y-1 text-sm">
                <li>• <strong>G-actin binding</strong> → supports cytoskeletal integrity and cell migration</li>
                <li>• <strong>Tβ4–ILK–PI3K–Akt</strong> → anti-apoptotic, pro-regenerative signaling</li>
                <li>• <strong>TGF-β modulation</strong> → anti-fibrotic via downregulation of collagen I/III</li>
                <li>• <strong>MMP/TIMP balance</strong> → matrix remodeling and scar inhibition</li>
              </ul>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">Preclinical Evidence:</h3>
              <ul className="text-green-700 space-y-1">
                <li>• Reduced fibrosis in heart, lungs, kidneys, liver</li>
                <li>• Better cardiac function recovery post-MI</li>
                <li>• Decreased lung collagen in pulmonary fibrosis</li>
                <li>• Shifts healing toward regeneration vs. scarring</li>
              </ul>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-2">Clinical Applications:</h3>
              <ul className="text-blue-700 space-y-1">
                <li>• TB-500: tendon/ligament injuries</li>
                <li>• Corneal healing (ophthalmology)</li>
                <li>• Chronic skin wounds</li>
                <li>• Cardiac repair (phase 1 trials)</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <h3 className="font-semibold text-orange-800 mb-2">Protocol:</h3>
            <p className="text-orange-700">TB-500: 5-10mg SC weekly, divided into 2 injections, for 4-6 weeks. Often front-loaded in first week. No significant side effects reported.</p>
          </div>
          
          <KeyTakeaway>
            TB4/TB-500 tilts the balance away from fibrosis toward regeneration, holding promise for treating fibrotic diseases of aging like pulmonary fibrosis and cardiac stiffening.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 13,
      title: "Tesamorelin: Anti-Fibrotic via Metabolic Change",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Tesamorelin: Anti-Fibrotic via Metabolic Change</h2>
          
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-2">NAFLD/NASH Anti-Fibrotic Effect:</h3>
              <p className="text-blue-700">1-year RCT in HIV patients showed tesamorelin halted liver fibrosis progression while placebo group worsened (assessed by biopsy)</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">Mechanism:</h3>
              <ul className="text-green-700 space-y-1">
                <li>• Reduces visceral fat and improves insulin sensitivity</li>
                <li>• Creates liver environment conducive to fibrosis reversal</li>
                <li>• Upregulates mitochondrial function genes</li>
                <li>• Downregulates inflammatory/fibrogenic pathways</li>
              </ul>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h3 className="font-semibold text-purple-800 mb-2">Broader Implications:</h3>
              <p className="text-purple-700">Demonstrates how targeting metabolic/endocrine pathways can alleviate downstream fibrosis - potential applications in muscle fibrosis (sarcopenia) and cardiac fibrosis</p>
            </div>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <h3 className="font-semibold text-yellow-800 mb-2">Development Status:</h3>
            <p className="text-yellow-700">Now in Phase III trials for NASH in both HIV and non-HIV populations. Dosage: 2mg daily. Must monitor for cancer history due to IGF-1 effects.</p>
          </div>
          
          <KeyTakeaway>
            Tesamorelin illustrates how shifting hormonal milieu (GH/IGF-1) can lead to reduced organ fibrosis, opening considerations for other fibrotic conditions of aging.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 14,
      title: "Timekeeping Domain: Epitalon",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Timekeeping Domain: Epitalon</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-cyan-50 p-4 rounded-lg border border-cyan-200">
                <h3 className="font-semibold text-cyan-800 mb-2">Primary Mechanisms:</h3>
                <ul className="text-cyan-700 space-y-1 text-sm">
                  <li>• Synthetic tetrapeptide (Ala-Glu-Asp-Gly)</li>
                  <li>• Derived from pineal gland</li>
                  <li>• Increases telomere length in aged cells</li>
                  <li>• Extends cellular Hayflick limit</li>
                </ul>
              </div>
              
              <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                <h3 className="font-semibold text-teal-800 mb-2">Key Signaling Pathways:</h3>
                <ul className="text-teal-700 space-y-1 text-sm">
                  <li>• <strong>Telomerase–hTERT expression</strong> → maintains telomere length</li>
                  <li>• <strong>Melatonin rhythm restoration</strong> → circadian-pituitary signaling</li>
                  <li>• <strong>Antioxidant enzymes (SOD, catalase)</strong> → oxidative stress resistance</li>
                  <li>• <strong>p53–p21–Rb axis modulation</strong> → DNA damage response control</li>
                </ul>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2">Circadian Reset:</h3>
                <ul className="text-green-700 space-y-1 text-sm">
                  <li>• Normalizes circadian hormone release</li>
                  <li>• Restores nightly melatonin peaks</li>
                  <li>• Improves deep sleep quality</li>
                  <li>• Enhances overnight repair processes</li>
                </ul>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">Animal Studies:</h3>
                <ul className="text-blue-700 space-y-1 text-sm">
                  <li>• 13% lifespan extension in mice</li>
                  <li>• Restored circadian rhythms in primates</li>
                  <li>• Enhanced immune surveillance</li>
                  <li>• Possible reduced cancer incidence</li>
                </ul>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2">Human Data (Limited):</h3>
                <ul className="text-purple-700 space-y-1 text-sm">
                  <li>• Improved sleep and melatonin output</li>
                  <li>• 50% lower 6-year mortality vs. controls</li>
                  <li>• Better cardiovascular health</li>
                  <li>• Anecdotal telomere lengthening</li>
                </ul>
              </div>
            </div>
          </div>
          
          <KeyTakeaway>
            Epitalon targets two fundamental aging clocks - genomic time (telomeres) and chronobiological time (circadian rhythm) - potentially turning back biological age.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 15,
      title: "Epitalon Clinical Protocol",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Epitalon Clinical Protocol</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">Khavinson Protocol:</h3>
                <ul className="text-blue-700 space-y-1">
                  <li>• 10mg daily for 10 days</li>
                  <li>• Divided: 5mg SC morning + 5mg evening</li>
                  <li>• Repeat every 6 months</li>
                  <li>• Alternative: 20 days straight annually</li>
                </ul>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2">Administration:</h3>
                <ul className="text-green-700 space-y-1">
                  <li>• Subcutaneous injection preferred</li>
                  <li>• Oral versions available (poor absorption)</li>
                  <li>• Reconstituted injectable form</li>
                  <li>• Store refrigerated</li>
                </ul>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h3 className="font-semibold text-yellow-800 mb-2">Safety Profile:</h3>
                <ul className="text-yellow-700 space-y-1">
                  <li>• Very safe, minimal side effects</li>
                  <li>• Rare injection site irritation</li>
                  <li>• No acute adverse events</li>
                  <li>• Contraindicated in active cancer</li>
                </ul>
              </div>
              
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h3 className="font-semibold text-red-800 mb-2">Regulatory Status:</h3>
                <ul className="text-red-700 space-y-1">
                  <li>• Not FDA-approved</li>
                  <li>• Banned from US compounding (2023)</li>
                  <li>• Available through research suppliers</li>
                  <li>• Cost: ~$300-600 per year</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-2">Patient Reports:</h3>
            <p className="text-gray-700">Improved sleep, vision, and overall vitality - aligning with biological clock restoration effects. Independent verification of Russian studies needed.</p>
          </div>
        </div>
      )
    },
    {
      id: 16,
      title: "Regulatory Landscape and Access",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Regulatory Landscape and Access</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2">FDA-Approved Peptides:</h3>
                <ul className="text-green-700 space-y-1 text-sm">
                  <li>• GLP-1 RAs (diabetes, obesity)</li>
                  <li>• Tesamorelin (HIV lipodystrophy)</li>
                  <li>• Well-characterized risk profiles</li>
                  <li>• Ready for clinical integration</li>
                </ul>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h3 className="font-semibold text-yellow-800 mb-2">Overseas Approved:</h3>
                <ul className="text-yellow-700 space-y-1 text-sm">
                  <li>• Thymosin α1 (~30 countries)</li>
                  <li>• Used for hepatitis, cancer adjuvant</li>
                  <li>• Decades of safety data</li>
                  <li>• Strong mechanistic rationale</li>
                </ul>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h3 className="font-semibold text-red-800 mb-2">2023 FDA Restrictions:</h3>
                <ul className="text-red-700 space-y-1 text-sm">
                  <li>• BPC-157, Thymosin α1, Epitalon</li>
                  <li>• Banned from compounding</li>
                  <li>• "Insufficient safety data" cited</li>
                  <li>• Research use only</li>
                </ul>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2">Current Access Pathways:</h3>
                <ul className="text-purple-700 space-y-1 text-sm">
                  <li>• IND clinical trials</li>
                  <li>• Patient importation</li>
                  <li>• Research chemical suppliers</li>
                  <li>• Quality/potency concerns</li>
                </ul>
              </div>
            </div>
          </div>
          
          <KeyTakeaway>
            Clinicians must balance potential benefits with regulatory and safety considerations, staying informed on legal pathways while many peptides await formal approval despite strong safety profiles.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 17,
      title: "Safety Considerations and Monitoring",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Safety Considerations and Monitoring</h2>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Peptide Class</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Key Monitoring</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contraindications</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Side Effects</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">GLP-1 RAs</td>
                  <td className="px-4 py-4 text-sm text-gray-700">HbA1c, amylase/lipase, weight</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Pancreatitis history, gastroparesis</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Nausea, GI upset (transient)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">GH Secretagogues</td>
                  <td className="px-4 py-4 text-sm text-gray-700">IGF-1, glucose, cancer screening</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Active cancer, acromegaly</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Edema, joint stiffness, insulin resistance</td>
                </tr>
                <tr>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Thymosin α1</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Immune markers, infection status</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Immunosuppressed transplant patients</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Mild injection site reactions</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">BPC-157</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Healing progress, function</td>
                  <td className="px-4 py-4 text-sm text-gray-700">None known</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Minimal (no LD50 found)</td>
                </tr>
                <tr>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">TB-500</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Tissue repair, inflammation markers</td>
                  <td className="px-4 py-4 text-sm text-gray-700">None established</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Occasional lethargy, headache</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Epitalon</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Sleep quality, energy, cancer screening</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Active cancer (theoretical risk)</td>
                  <td className="px-4 py-4 text-sm text-gray-700">Rare injection site irritation</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-2">General Principles:</h3>
            <ul className="text-blue-700 space-y-1">
              <li>• Start with lowest effective dose</li>
              <li>• Use cycling protocols when appropriate</li>
              <li>• Monitor biomarkers specific to peptide class</li>
              <li>• Consider drug interactions and patient comorbidities</li>
              <li>• Source from reputable suppliers when using research peptides</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 18,
      title: "Clinical Integration Strategies",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Clinical Integration Strategies</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2">Start with FDA-Approved:</h3>
                <ul className="text-green-700 space-y-1 text-sm">
                  <li>• GLP-1 RAs for metabolic optimization</li>
                  <li>• Tesamorelin for visceral adiposity</li>
                  <li>• Build experience with known safety profiles</li>
                  <li>• Establish monitoring protocols</li>
                </ul>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">Patient Selection:</h3>
                <ul className="text-blue-700 space-y-1 text-sm">
                  <li>• Comprehensive health assessment</li>
                  <li>• Risk-benefit analysis</li>
                  <li>• Informed consent process</li>
                  <li>• Realistic expectation setting</li>
                </ul>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2">Combination Approaches:</h3>
                <ul className="text-purple-700 space-y-1 text-sm">
                  <li>• Target multiple SHIFT domains</li>
                  <li>• Synergistic peptide combinations</li>
                  <li>• Integrate with lifestyle interventions</li>
                  <li>• Personalized protocols</li>
                </ul>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h3 className="font-semibold text-yellow-800 mb-2">Documentation & Tracking:</h3>
                <ul className="text-yellow-700 space-y-1 text-sm">
                  <li>• Baseline and follow-up biomarkers</li>
                  <li>• Functional assessments</li>
                  <li>• Adverse event reporting</li>
                  <li>• Outcome measurements</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="flex justify-center">
            <svg width="400" height="250" viewBox="0 0 400 250" className="border border-gray-200 rounded-lg">
              {/* Clinical Integration Flowchart */}
              <rect x="150" y="20" width="100" height="40" fill="#3b82f6" opacity="0.7" rx="8" />
              <text x="200" y="45" textAnchor="middle" className="fill-white font-semibold text-sm">Assessment</text>
              
              <rect x="50" y="90" width="80" height="35" fill="#10b981" opacity="0.7" rx="6" />
              <text x="90" y="110" textAnchor="middle" className="fill-white font-semibold text-xs">FDA-Approved</text>
              
              <rect x="270" y="90" width="80" height="35" fill="#f59e0b" opacity="0.7" rx="6" />
              <text x="310" y="110" textAnchor="middle" className="fill-white font-semibold text-xs">Investigational</text>
              
              <rect x="150" y="160" width="100" height="40" fill="#8b5cf6" opacity="0.7" rx="8" />
              <text x="200" y="180" textAnchor="middle" className="fill-white font-semibold text-xs">Monitor &</text>
              <text x="200" y="195" textAnchor="middle" className="fill-white font-semibold text-xs">Adjust</text>
              
              {/* Arrows */}
              <path d="M200 60 L90 90" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead)" />
              <path d="M200 60 L310 90" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead)" />
              <path d="M90 125 L180 160" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead)" />
              <path d="M310 125 L220 160" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead)" />
            </svg>
          </div>
        </div>
      )
    },
    {
      id: 19,
      title: "Future Directions and Emerging Peptides",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Future Directions and Emerging Peptides</h2>
          
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-2">Ongoing Clinical Trials (2025):</h3>
              <ul className="text-blue-700 space-y-1">
                <li>• GLP-1 RAs in Alzheimer's disease</li>
                <li>• MOTS-c analogs (CB4211) in obesity/NASH</li>
                <li>• Thymosin α1 in critical care and oncology</li>
                <li>• Epitalon in age-related macular degeneration</li>
                <li>• Tesamorelin Phase III for NASH</li>
              </ul>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">Emerging Peptide Classes:</h3>
              <ul className="text-green-700 space-y-1">
                <li>• Senolytic peptides (FOXO4-DRI for senescent cell removal)</li>
                <li>• Mitochondrial-derived peptides (humanin, MOTS-c analogs)</li>
                <li>• Clock-modulating peptides (VIP analogs)</li>
                <li>• Relaxin and angiotensin 1-7 for anti-fibrosis</li>
                <li>• Telomerase mimetics beyond epitalon</li>
              </ul>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h3 className="font-semibold text-purple-800 mb-2">Technology Advances:</h3>
              <ul className="text-purple-700 space-y-1">
                <li>• Improved peptide stability and delivery</li>
                <li>• Oral bioavailability enhancements</li>
                <li>• Targeted delivery systems</li>
                <li>• Personalized peptide protocols</li>
                <li>• Biomarker-guided therapy selection</li>
              </ul>
            </div>
          </div>
          
          <KeyTakeaway>
            The field is rapidly evolving with more rigorous clinical trials anticipated. Clinicians should stay informed about new peptide therapies while current tools already offer significant potential for healthspan extension.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 20,
      title: "Case Study: Integrated Peptide Protocol",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Case Study: Integrated Peptide Protocol</h2>
          
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Patient Profile:</h3>
            <p className="text-gray-700 mb-4">58-year-old executive with metabolic syndrome, chronic fatigue, poor recovery from exercise, and elevated inflammatory markers</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-semibold text-red-800 mb-2">Baseline Markers:</h4>
                <ul className="text-red-700 space-y-1 text-sm">
                  <li>• HbA1c: 6.2%</li>
                  <li>• Visceral fat: elevated</li>
                  <li>• CRP: 4.2 mg/L</li>
                  <li>• IGF-1: low-normal</li>
                  <li>• Poor sleep quality</li>
                </ul>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-2">SHIFT-Based Protocol:</h4>
                <ul className="text-blue-700 space-y-1 text-sm">
                  <li>• <strong>Sensing:</strong> Semaglutide 0.25→1.0mg weekly</li>
                  <li>• <strong>Homeostatic:</strong> Tesamorelin 1mg nightly</li>
                  <li>• <strong>Inflammation:</strong> TA1 1.6mg 2x/week</li>
                  <li>• <strong>Timekeeping:</strong> Epitalon 10-day cycle q6mo</li>
                </ul>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 mt-4">
              <h4 className="font-semibold text-green-800 mb-2">6-Month Outcomes:</h4>
              <ul className="text-green-700 space-y-1">
                <li>• HbA1c: 5.4% (15% weight loss)</li>
                <li>• Visceral fat: 30% reduction</li>
                <li>• CRP: 1.8 mg/L</li>
                <li>• IGF-1: mid-normal range</li>
                <li>• Improved sleep, energy, exercise recovery</li>
              </ul>
            </div>
          </div>
          
          <KeyTakeaway>
            Integrated SHIFT-based protocols targeting multiple aging mechanisms simultaneously can achieve comprehensive metabolic, inflammatory, and functional improvements.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 21,
      title: "Conclusion: Peptides in Longevity Medicine",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Conclusion: Peptides in Longevity Medicine</h2>
          
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-2">Multi-Pronged Strategy:</h3>
              <p className="text-blue-700">Therapeutic peptides offer targeted interventions for healthspan extension by restoring youthful signaling networks with high specificity and low toxicity</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">SHIFT Framework Application:</h3>
              <p className="text-green-700">Systematic approach to address complex aging biology: re-sensitizing nutrient pathways, defending homeostasis, quieting inflammation, preventing fibrosis, and resetting cellular time</p>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h3 className="font-semibold text-purple-800 mb-2">Clinical Implementation:</h3>
              <p className="text-purple-700">Balance potential benefits with regulatory considerations, starting with FDA-approved options and expanding to investigational peptides through appropriate pathways</p>
            </div>
            
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h3 className="font-semibold text-yellow-800 mb-2">Future Outlook:</h3>
              <p className="text-yellow-700">Rapid field evolution with more rigorous trials anticipated. Each peptide targets specific aging hallmarks, and when used judiciously in combination with lifestyle interventions, holds promise for significant quality of life improvements</p>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-lg mt-6">
            <h3 className="text-xl font-semibold mb-2">Key Message:</h3>
            <p className="text-lg">Peptides represent precision tools for longevity medicine - each one a targeted intervention to restore youthful physiology and extend healthspan through evidence-based protocols.</p>
          </div>
        </div>
      )
    },
    {
      id: 22,
      title: "References & Further Reading",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">References & Further Reading</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-800 mb-3">Key Clinical Studies:</h3>
              <div className="space-y-2 text-sm text-gray-700">
                <p>1. GLP-1 RAs: STEP trials, SELECT-CVOT</p>
                <p>2. Tesamorelin: HIV lipodystrophy trials, NAFLD studies</p>
                <p>3. Thymosin α1: Hepatitis trials, COVID-19 studies</p>
                <p>4. BPC-157: Ulcerative colitis Phase II</p>
                <p>5. TB-4: Cardiac repair trials, corneal healing</p>
                <p>6. Epitalon: Russian longevity studies</p>
                <p>7. MOTS-c: Preclinical aging studies, CB4211 trials</p>
              </div>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-3">Regulatory Resources:</h3>
              <div className="space-y-2 text-sm text-blue-700">
                <p>• FDA guidance on peptide compounding</p>
                <p>• International peptide approval databases</p>
                <p>• Clinical trial registries (ClinicalTrials.gov)</p>
                <p>• Professional society guidelines</p>
                <p>• Safety monitoring protocols</p>
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h3 className="font-semibold text-green-800 mb-3">Continuing Education:</h3>
            <ul className="text-green-700 space-y-1">
              <li>• Stay updated on emerging trial data</li>
              <li>• Monitor regulatory changes</li>
              <li>• Attend longevity medicine conferences</li>
              <li>• Network with experienced practitioners</li>
              <li>• Participate in peptide-focused medical education</li>
            </ul>
          </div>
          
          <div className="text-center bg-gray-100 p-6 rounded-lg">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Thank You</h3>
            <p className="text-gray-600">Questions & Discussion</p>
          </div>
        </div>
      )
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
      <SignedIn>
        <div className={`min-h-screen bg-white ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
          {!isFullscreen && (
            <div className="bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Link href="/" className="flex items-center hover:text-blue-600 transition-colors">
                  <Home className="w-4 h-4 mr-1" />
                  Home
                </Link>
                <span>/</span>
                <Link href="/mentorship" className="flex items-center hover:text-blue-600 transition-colors">
                  <Book className="w-4 h-4 mr-1" />
                  Mentorship
                </Link>
                <span>/</span>
                <span className="text-gray-900 font-medium">Peptides: A SHIFT Approach</span>
              </div>
            </div>
          )}

          <div className="max-w-6xl mx-auto px-6 py-8">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              {/* Slideshow Header */}
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h1 className="text-2xl font-bold">Peptides: A SHIFT Approach</h1>
                    <p className="text-blue-100 mt-2">Slide {currentSlide + 1} of {slides.length}</p>
                  </div>
                  <button
                    onClick={toggleFullscreen}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 p-2 rounded-lg transition-colors"
                  >
                    <Maximize2 className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Progress Bar */}
                <div className="mt-4 bg-white bg-opacity-20 rounded-full h-2">
                  <div 
                    className="bg-white h-2 rounded-full transition-all duration-300"
                    style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
                  />
                </div>
              </div>

              {/* Slide Content */}
              <div className="p-8 min-h-[600px]">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">{slides[currentSlide].title}</h2>
                  {slides[currentSlide].content}
                </div>
              </div>

              {/* Navigation Controls */}
              <div className="bg-gray-50 px-6 py-4 flex justify-between items-center">
                <button
                  onClick={prevSlide}
                  disabled={currentSlide === 0}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Previous</span>
                </button>

                {/* Slide Dots */}
                <div className="flex space-x-2 max-w-md overflow-x-auto">
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        index === currentSlide ? 'bg-blue-600' : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                    />
                  ))}
                </div>

                <button
                  onClick={nextSlide}
                  disabled={currentSlide === slides.length - 1}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  <span>Next</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </SignedIn>
    </>
  );
}